/** Approvals addressed to the signed-in employee.
 *
 * This route rendered an honest "no API yet" state until the full-site audit,
 * and the state had stopped being honest: `GET /api/portal/approvals/` and
 * `POST /api/portal/approvals/{id}/` both exist and work. A screen that says a
 * capability is missing when it is not is the same failure as a fabricated
 * number — somebody reads it and plans around it — so it is wired to the real
 * feed here.
 *
 * ## Why a manager approves in the portal
 *
 * A line manager is an ordinary Contact who never signs in to the service desk.
 * `RequestApproval.approver` is a Contact for exactly that reason, and the
 * decision endpoint lives under `/api/portal/` rather than `/api/agent/`.
 * Holding `approval.view_own` grants nothing on its own: the queryset is scoped
 * to approvals whose approver *is* the caller, so this screen can only ever
 * show what was addressed to this person.
 *
 * ## Rejection needs a reason
 *
 * The server refuses a rejection with an empty comment, and so does this form,
 * before the round trip. A request refused with no reason leaves the requester
 * with nothing to act on, and the desk fielding the question instead.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, ClipboardCheck, Clock, XCircle } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { PageHeader } from '../../components/layout/AppShell'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Callout, ErrorNote } from '../../components/ui/callout'
import { Card } from '../../components/ui/card'
import { EmptyState } from '../../components/ui/empty-state'
import { Label, Skeleton, Tabs, TabsList, TabsTrigger, Textarea } from '../../components/ui/primitives'
import { ApiError, api } from '../../lib/api'
import { APPROVAL_STATUS_LABELS, type Approval, type ApprovalStatus } from '../../lib/types'
import { formatDate } from '../../lib/ui'

const TABS = [
  { key: 'pending', label: 'Waiting on you' },
  { key: 'decided', label: 'Decided' },
] as const

type TabKey = (typeof TABS)[number]['key']

/** Which badge a state earns. `expired` is deliberately not neutral: nobody
 *  decided, and that is a refusal, not a quiet nothing. */
const TONE: Record<ApprovalStatus, 'warning' | 'success' | 'critical' | 'neutral'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'critical',
  cancelled: 'neutral',
  expired: 'critical',
}

export function Approvals() {
  const [tab, setTab] = useState<TabKey>('pending')
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-approvals'],
    queryFn: () => api.get<Approval[]>('/approvals/'),
  })

  const rows = (data ?? []).filter((row) =>
    tab === 'pending' ? row.status === 'pending' : row.status !== 'pending',
  )
  const waiting = (data ?? []).filter((row) => row.status === 'pending').length

  return (
    <div>
      <PageHeader
        title="Approvals"
        description="Requests from your team that need your decision."
      />

      {error && (
        <ErrorNote
          className="mb-4"
          message={
            error instanceof ApiError && error.status === 403
              ? 'Your account is not set up to decide approvals. Ask IT to check your role.'
              : 'Approvals could not be loaded. Try again in a moment.'
          }
        />
      )}

      <div className="mb-4">
        <Tabs value={tab} onValueChange={(value) => setTab(value as TabKey)}>
          <TabsList>
            {TABS.map((option) => (
              <TabsTrigger key={option.key} value={option.key}>
                {option.label}
                {option.key === 'pending' && waiting > 0 && (
                  <Badge variant="warning" size="sm" className="ml-2">
                    {waiting}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title={tab === 'pending' ? 'Nothing waiting on you' : 'Nothing decided yet'}
          description={
            tab === 'pending'
              ? 'When somebody who reports to you raises a request that needs sign-off, it will appear here and you will get an email.'
              : 'Approvals you have approved or rejected are kept here.'
          }
        />
      ) : (
        <ul className="space-y-3">
          {rows.map((approval) => (
            <li key={approval.id}>
              <ApprovalCard approval={approval} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ApprovalCard({ approval }: { approval: Approval }) {
  const queryClient = useQueryClient()
  const [comment, setComment] = useState('')
  const [reasonMissing, setReasonMissing] = useState(false)

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'reject') =>
      api.postJson<Approval>(`/approvals/${approval.id}/`, { decision, comment }),
    onSuccess: () => {
      setComment('')
      void queryClient.invalidateQueries({ queryKey: ['portal-approvals'] })
      void queryClient.invalidateQueries({ queryKey: ['tickets'] })
    },
  })

  const pending = approval.status === 'pending'
  const fields = Object.entries(approval.form_data ?? {}).filter(
    ([, value]) => value !== null && value !== '',
  )

  const reject = () => {
    // Checked here as well as on the server. The round trip would refuse it
    // anyway; refusing before it is the difference between a form that
    // explains itself and one that reports an error.
    if (!comment.trim()) {
      setReasonMissing(true)
      return
    }
    setReasonMissing(false)
    decide.mutate('reject')
  }

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/tickets/${approval.ticket_ref}`}
              className="numeric font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              {approval.ticket_ref}
            </Link>
            <Badge variant={TONE[approval.status]} size="sm">
              {APPROVAL_STATUS_LABELS[approval.status]}
            </Badge>
          </div>
          <h2 className="mt-1 text-sm font-semibold text-foreground">{approval.ticket_title}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {approval.requester_name} ({approval.requester_email})
            {approval.service && ` · ${approval.service.service} — ${approval.service.subcategory}`}
          </p>
        </div>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="size-3.5" aria-hidden />
          Raised {formatDate(approval.requested_at)}
        </p>
      </div>

      {approval.ticket_description && (
        <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
          {approval.ticket_description}
        </p>
      )}

      {fields.length > 0 && (
        <dl className="mt-3 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
          {fields.map(([key, value]) => (
            <div key={key} className="flex gap-2">
              <dt className="shrink-0 font-medium text-foreground">{key}</dt>
              <dd className="min-w-0 truncate text-muted-foreground">{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {approval.expires_at && pending && (
        <Callout tone="warning" className="mt-3">
          Decide by {formatDate(approval.expires_at)}. An approval nobody decides expires — it is
          never treated as approved.
        </Callout>
      )}

      {pending ? (
        <div className="mt-4 border-t border-border pt-3">
          <Label htmlFor={`comment-${approval.id}`}>
            Comment <span className="text-muted-foreground">(required to reject)</span>
          </Label>
          <Textarea
            id={`comment-${approval.id}`}
            className="mt-1"
            rows={2}
            value={comment}
            onChange={(event) => {
              setComment(event.target.value)
              if (reasonMissing) setReasonMissing(false)
            }}
            placeholder="Why you are approving or refusing this."
            aria-invalid={reasonMissing || undefined}
            aria-describedby={reasonMissing ? `comment-error-${approval.id}` : undefined}
          />
          {reasonMissing && (
            <p
              id={`comment-error-${approval.id}`}
              role="alert"
              className="mt-1 text-xs text-critical"
            >
              Give a reason so {approval.requester_name.split(' ')[0]} knows what to do next.
            </p>
          )}

          {decide.error && (
            <ErrorNote
              className="mt-2"
              message={
                decide.error instanceof ApiError
                  ? decide.error.detail
                  : 'The decision could not be recorded.'
              }
            />
          )}

          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={() => decide.mutate('approve')} disabled={decide.isPending}>
              <CheckCircle2 />
              Approve
            </Button>
            <Button size="sm" variant="outline" onClick={reject} disabled={decide.isPending}>
              <XCircle />
              Reject
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
          {APPROVAL_STATUS_LABELS[approval.status]}
          {approval.decided_by_name && ` by ${approval.decided_by_name}`}
          {approval.decided_at && ` on ${formatDate(approval.decided_at)}`}
          {approval.comment && <p className="mt-1 text-foreground">“{approval.comment}”</p>}
        </div>
      )}
    </Card>
  )
}
